import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { throwErrorMessage } from "../../../helpers/kwikui.common.helpers";
import { DEFAULT_VALUES, VALIDATE_KEY_VALUES } from "./input-email.constants";
import {
  TKwikUIInputEmailCase,
  TKwikUIInputEmailSize
} from "./input-email.definitions";
import { isValidKeyValue } from "./input-email.validation";

@Component({
  selector: "kwikui-input-email",
  templateUrl: "./input-email.component.html",
  styleUrls: ["./input-email.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KwikUIInputEmailComponent),
      multi: true
    }
  ]
})
export class KwikUIInputEmailComponent
  implements OnInit, OnChanges, ControlValueAccessor {
  @Input() case: TKwikUIInputEmailCase = DEFAULT_VALUES.case;

  @Input() disabled: boolean = DEFAULT_VALUES.disabled;

  @Input() domains: string[] = DEFAULT_VALUES.domains;

  @Input() focus: boolean = DEFAULT_VALUES.focus;

  @Input() formControl: FormControl = new FormControl({});

  @Input() formControlName: string = DEFAULT_VALUES.formControlName;

  @Input() hintContent: string = DEFAULT_VALUES.hintContent;

  @Input() icon: string = DEFAULT_VALUES.icon;

  @Input() id: string = DEFAULT_VALUES.id;

  @Input() invalid: boolean = DEFAULT_VALUES.invalid;

  @Input() label: string = DEFAULT_VALUES.label;

  @Input() placeholder: string = DEFAULT_VALUES.placeholder;

  @Input() postfix: string = DEFAULT_VALUES.postfix;

  @Input() prefix: string = DEFAULT_VALUES.prefix;

  @Input() properties = { readOnly: false };

  @Input() size: TKwikUIInputEmailSize = DEFAULT_VALUES.size;

  @Input() validators = { required: true };

  @Output() getKeyValue: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("inputEmail") inputEmail: ElementRef;

  @ViewChild("dropdown") dropdown: ElementRef;

  dropdownPosition: string = undefined;

  suggestions: string[] = [];

  domainSelected: string = undefined;

  formGroup: FormGroup = new FormGroup({});

  subscriptions = new Subscription();

  private inputSubject = new Subject<string>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.formGroup.addControl(this.formControlName, this.formControl);
    this.focus = this.focus === undefined ? false : this.focus;
    this.invalid =
      this.invalid === undefined ? false : this.invalid && this.focus;
    this.validators = { ...this.validators };
    this.properties = { ...this.properties };
    this.changeCase();
    this.setDisabled();

    this.subscriptions.add(
      this.formGroup.controls[this.formControlName].valueChanges
        .pipe(distinctUntilChanged()) // makes sure the value has actually changed.
        .subscribe((value) => this.handleInputValueChange(value))
    );

    this.inputSubject.pipe(debounceTime(300)).subscribe(() => {
      this.updateSuggestions();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const verifyChange = (key: string) => {
      return changes.hasOwnProperty(key) && !changes[key].firstChange;
    };

    for (const change of Object.entries(changes)) {
      const key: string = change[0];
      const value: any = change[1].currentValue;
      this.validateInputProperty(key, value);
    }

    if (verifyChange("formControl")) {
      this.formControl = changes.formControl.currentValue;
    }
    if (verifyChange("invalid")) {
      this.invalid = Boolean(changes.invalid.currentValue);
      this.setError();
    }
    if (verifyChange("focus")) {
      this.focus = Boolean(changes.focus.currentValue);
    }
    if (verifyChange("disabled")) {
      this.disabled = Boolean(changes.disabled.currentValue);
      this.setDisabled();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // ensure when component is destroyed the subscription is also and not left open.
  }

  private validateInputProperty(key: string, value: any): void {
    if (VALIDATE_KEY_VALUES[key] && !isValidKeyValue(key, value)) {
      this[key] = DEFAULT_VALUES[key];
      throwErrorMessage(
        "kwikui-input-email",
        this.id,
        key,
        value,
        DEFAULT_VALUES[key]
      );
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setDisabled() {
    if (this.formGroup.controls[this.formControlName] !== undefined) {
      if (this.disabled === true) {
        this.formGroup.controls[this.formControlName].disable({
          emitEvent: false
        });
      } else {
        this.formGroup.controls[this.formControlName].enable({
          emitEvent: false
        });
      }
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setError() {
    if (
      this.formGroup.controls[this.formControlName] !== undefined &&
      this.formGroup.controls[this.formControlName].invalid
    ) {
      this.invalid = true;
      this.focus = true;
    } else {
      this.invalid = false;
      this.focus = false;
    }
  }

  /**
   * @description Handle changing of case based on 'case' props
   */
  changeCase() {
    switch (this.case) {
      case "upper":
        this.formGroup.patchValue({
          [this.formControlName]:
            this.formGroup.controls[
              this.formControlName
            ].value.toLocaleUpperCase()
        });
        break;
      case "lower":
        this.formGroup.patchValue({
          [this.formControlName]:
            this.formGroup.controls[
              this.formControlName
            ].value.toLocaleLowerCase()
        });
        break;
      default:
        break;
    }
  }

  /**
   * @description Handles firing of 2 events on (keyup) event
   *
   * @param value
   */
  handleInputValueChange(value: any) {
    this.changeCase();
    this.inputSubject.next(this.formControl.value);
    if (value === this.formControl.value) {
      this.emitEvent(this.getKeyValue, {
        key: this.formControlName,
        value: this.formControl.value
      });
    }
  }

  /**
   * @description Update the suggestion domains
   */
  updateSuggestions() {
    const atIndex = this.formControl.value.lastIndexOf("@");
    this.suggestions = [];

    if (!this.domainSelected && atIndex != -1) {
      const domainInput = this.formControl.value.substring(atIndex + 1);
      this.suggestions = this.domains.filter((domain) =>
        domain.startsWith(domainInput)
      );
      this.cdr.detectChanges();
      this.checkDropdownPosition();
    }

    this.domainSelected = undefined;
  }

  /**
   * @description Handles Position of Suggestion Dropdown
   */
  checkDropdownPosition() {
    if (this.dropdown) {
      const rectRelative =
        this.inputEmail.nativeElement.getBoundingClientRect();
      const rectAbsolute = this.dropdown.nativeElement.getBoundingClientRect();

      if (rectAbsolute.bottom + rectRelative.height > window.innerHeight) {
        this.dropdownPosition = `-${rectAbsolute.height + 8}px`;
      } else {
        this.dropdownPosition = "100%";
      }
    }
  }

  /**
   * @description Handle selection of domains
   */
  selectDomain(domain: string) {
    this.formControl.setValue(
      this.formControl.value.replace(/@[\w.-]*$/, `@${domain}`)
    );
    this.suggestions = [];
    this.domainSelected = domain;
  }

  handleFocusedChange(e: any) {
    if (!this.invalid) {
      this.focus = e;
    }
  }

  emitEvent(event: any, data: any) {
    event.emit(data);
  }

  /** Method Implementations for Abstract Control */
  writeValue(value: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
